import type { Circuit } from '@multirx/shared/types/track.js';
import type { RaceHistorySummary } from '@multirx/shared/types/raceHistory.js';
import { DIVISIONS } from '@multirx/shared/types/division.js';
import { fetchCircuitById } from '../circuits/api.js';
import { mountLazyCircuit3dPreview } from '../circuits/circuit3dLazyPreview.js';
import { circuitDisplayTitle } from '../circuits/circuitDisplay.js';
import { resolveMediaUrl } from '../mediaUrl.js';
import { createCardEarthLoader } from '../ui/earthLoader.js';
import { dataUrlToImageBlob, hasStoredArchivePreview, imageMimeToExt } from './archivePreview.js';
import { uploadRaceHistoryPreview } from './api.js';
import { archivesDetailHash } from './archivesHash.js';

const DICE_ICON_SVG = `<svg class="circuit-card-dice-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" opacity="0.25"/><rect x="3" y="3" width="18" height="18" rx="3" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="8" cy="8" r="1.35" fill="currentColor"/><circle cx="16" cy="8" r="1.35" fill="currentColor"/><circle cx="12" cy="12" r="1.35" fill="currentColor"/><circle cx="8" cy="16" r="1.35" fill="currentColor"/><circle cx="16" cy="16" r="1.35" fill="currentColor"/></svg>`;

/** Évite les uploads doublons si plusieurs cartes re-montent la même archive. */
const previewUploadInFlight = new Set<number>();

function divisionLabel(id: number): string {
  return DIVISIONS.find((d) => d.id === id)?.shortLabel ?? `D${id}`;
}

function formatCircuitTitle(item: RaceHistorySummary): string {
  if (item.circuitId == null) return 'Circuit non enregistré';
  return circuitDisplayTitle({ id: item.circuitId, name: item.circuitName });
}

function formatLapsLabel(numLaps: number): string {
  return `${numLaps} tour${numLaps > 1 ? 's' : ''}`;
}

function formatDiceRoundLabel(turnCount: number): string {
  return `${turnCount} tour${turnCount > 1 ? 's' : ''}`;
}

async function resolveCircuitForPreview(item: RaceHistorySummary): Promise<Circuit | null> {
  if (item.circuitSnapshot?.path?.length) return item.circuitSnapshot;
  if (item.circuitId == null) return null;
  try {
    const saved = await fetchCircuitById(item.circuitId);
    return saved?.circuit?.path?.length ? saved.circuit : null;
  } catch {
    return null;
  }
}

function showPreviewLoader(preview: HTMLElement, wrap: HTMLElement): void {
  if (!wrap.querySelector('.circuit-preview-earth-loader')) {
    wrap.appendChild(createCardEarthLoader('Chargement…'));
  }
  preview.classList.add('circuit-preview--loading');
  wrap.classList.add('is-loading');
}

function clearPreviewLoader(preview: HTMLElement, wrap: HTMLElement): void {
  wrap.querySelector('.circuit-preview-earth-loader')?.remove();
  preview.querySelector('.circuit-preview-earth-loader')?.remove();
  preview.classList.remove('circuit-preview--loading');
  wrap.classList.remove('is-loading');
}

function mountStoredPreviewImage(
  preview: HTMLElement,
  wrap: HTMLElement,
  previewImagePath: string,
  onBroken: () => void
): void {
  clearPreviewLoader(preview, wrap);
  wrap.classList.remove('circuit-card-preview-wrap--empty', 'is-loading');
  preview.classList.remove('circuit-preview--loading');
  const img = document.createElement('img');
  img.className = 'circuit-card-3d-snapshot';
  img.alt = '';
  img.decoding = 'async';
  img.loading = 'lazy';
  img.addEventListener(
    'error',
    () => {
      img.remove();
      onBroken();
    },
    { once: true }
  );
  img.src = resolveMediaUrl(previewImagePath);
  preview.replaceChildren(img);
}

function mountGeneratedPreview(
  item: RaceHistorySummary,
  preview: HTMLElement,
  previewWrap: HTMLElement,
  setDispose: (dispose: (() => void) | null) => void,
  disposed: () => boolean
): void {
  showPreviewLoader(preview, previewWrap);
  void resolveCircuitForPreview(item).then((circuit) => {
    if (disposed()) return;
    if (!circuit?.path?.length) {
      clearPreviewLoader(preview, previewWrap);
      previewWrap.classList.add('circuit-card-preview-wrap--empty');
      return;
    }
    previewWrap.classList.remove('circuit-card-preview-wrap--empty');
    setDispose(
      mountLazyCircuit3dPreview(preview, circuit, null, item.circuitName, {
        snapshot: true,
        loadingMessage: 'Chargement…',
        onSnapshot: (dataUrl) => schedulePreviewBackfill(item.id, dataUrl),
      })
    );
  });
}

function schedulePreviewBackfill(archiveId: number, dataUrl: string): void {
  if (previewUploadInFlight.has(archiveId)) return;
  const blob = dataUrlToImageBlob(dataUrl);
  if (!blob) return;
  previewUploadInFlight.add(archiveId);
  const ext = imageMimeToExt(blob.type);
  void uploadRaceHistoryPreview(archiveId, blob, `preview${ext}`)
    .catch(() => {
      /* best-effort : la prochaine visite retentera le rendu 3D */
    })
    .finally(() => {
      previewUploadInFlight.delete(archiveId);
    });
}

export interface ArchiveRaceCard {
  card: HTMLAnchorElement;
  /** Démonte la vignette 3D — à appeler avant de retirer la carte du DOM. */
  dispose: () => void;
}

export function createArchiveRaceCard(
  item: RaceHistorySummary,
  onNavigate: (id: number) => void
): ArchiveRaceCard {
  const circuitTitle = formatCircuitTitle(item);
  const lapsLabel = formatLapsLabel(item.numLaps);
  const diceRoundLabel = formatDiceRoundLabel(item.turnCount);

  const card = document.createElement('a');
  card.className =
    'circuit-card circuit-card--tile circuit-card--interactive circuit-card--archive';
  card.href = `#${archivesDetailHash(item.id)}`;
  card.setAttribute('role', 'listitem');
  card.setAttribute(
    'aria-label',
    `${circuitTitle}, ${divisionLabel(item.division)}, ${lapsLabel}, ${diceRoundLabel} de lancer de dé`
  );
  card.addEventListener('click', (e) => {
    e.preventDefault();
    onNavigate(item.id);
  });

  const previewWrap = document.createElement('div');
  previewWrap.className = 'circuit-card-preview-wrap is-loading';

  const preview = document.createElement('div');
  preview.className = 'circuit-card-preview circuit-preview--loading';
  preview.setAttribute('aria-hidden', 'true');

  const circuitLabel = document.createElement('span');
  circuitLabel.className =
    'circuit-card-overlay circuit-card-overlay--tl circuit-card-overlay-chip circuit-card-overlay-chip--circuit';
  circuitLabel.textContent = circuitTitle;
  circuitLabel.title = circuitTitle;

  const divisionBadge = document.createElement('span');
  divisionBadge.className = `circuit-card-overlay circuit-card-overlay--tr circuit-card-overlay-chip circuit-card-overlay-chip--division badge badge-division--${item.division}`;
  divisionBadge.textContent = divisionLabel(item.division);

  const lapsEl = document.createElement('span');
  lapsEl.className =
    'circuit-card-overlay circuit-card-overlay--bl circuit-card-overlay-chip circuit-card-overlay-chip--laps';
  lapsEl.textContent = lapsLabel;

  const diceEl = document.createElement('span');
  diceEl.className =
    'circuit-card-overlay circuit-card-overlay--br circuit-card-overlay-chip circuit-card-overlay-chip--dice circuit-card-overlay--dice';
  diceEl.innerHTML = `${DICE_ICON_SVG}<span class="circuit-card-dice-count">${diceRoundLabel}</span>`;

  previewWrap.append(preview, divisionBadge, circuitLabel, lapsEl, diceEl);
  card.appendChild(previewWrap);

  let disposed = false;
  let disposePreview: (() => void) | null = null;
  const isDisposed = (): boolean => disposed;
  const setDispose = (fn: (() => void) | null): void => {
    disposePreview = fn;
  };

  if (hasStoredArchivePreview(item.previewImagePath)) {
    mountStoredPreviewImage(preview, previewWrap, item.previewImagePath!, () => {
      if (disposed) return;
      mountGeneratedPreview(item, preview, previewWrap, setDispose, isDisposed);
    });
  } else {
    mountGeneratedPreview(item, preview, previewWrap, setDispose, isDisposed);
  }

  return {
    card,
    dispose: () => {
      disposed = true;
      disposePreview?.();
    },
  };
}
